'use client';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { ButtonIcon } from '../ui/button-icon';
import { LuX } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu1?: Menu;
  menu2?: Menu;
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  generalLayout: GeneralLayout['data'];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const phoneHref = props.generalLayout?.contact?.phone?.replace(/\s+/g, '');

  return (
    <>
      <Sheet>
        <SheetTrigger
          className={cn('ea-mobile-sidebar__trigger', props.className?.trigger)}
          aria-label={t('text3241')}
        >
          <ButtonIcon
            alt=""
            url="/images/icons/sidebar-black.svg"
            className="max-h-6 min-h-6 min-w-6 max-w-6"
          />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          overlayClassName="bg-black/25 backdrop-blur-none"
          side={'left'}
          className={cn(
            'hide-scroll-bar flex w-full max-w-[342px] flex-col items-center justify-start gap-6 overflow-y-scroll border-none p-0 shadow-none',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'hidden',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">{t('text3241')}</SheetTitle>
          <div className="flex w-full items-center justify-end px-4 py-6">
            <SheetClose aria-label={t('text272')}>
              <LuX size={20} aria-hidden />
            </SheetClose>
          </div>
          {props.menu2 &&
            props.menu2?.fields?.map(item => (
              <SheetClose
                key={item.name}
                asChild
                className={cn('h4', 'text-center font-semibold tracking-tight')}
              >
                <Link prefetchOnHover prefetch={false} href={getSafeHref(item?.path ?? '#')}>
                  {item.name}
                </Link>
              </SheetClose>
            ))}
          <div className="w-full px-5">
            <Separator className="h-[1px] w-full bg-border" />
          </div>
          {props.menu1 &&
            props.menu1?.fields?.map(item => (
              <SheetClose
                key={item.name}
                asChild
                className={cn('text-center text-sm font-semibold tracking-tight')}
              >
                <Link prefetchOnHover prefetch={false} href={getSafeHref(item?.path ?? '#')}>
                  {item.name}
                </Link>
              </SheetClose>
            ))}
          <div className="w-full px-5">
            <Link
              prefetchOnHover
              prefetch={false}
              href={`tel:${phoneHref}`}
              className="flex w-full items-center justify-start gap-3 rounded-[12px] bg-primary-foreground p-3"
            >
              <div className="flex h-12 min-w-12 max-w-12 items-center justify-center rounded-full bg-white">
                <ButtonIcon alt="Volať" url="/images/icons/phone-primary.svg" />
              </div>
              <div className="flex w-full flex-col items-start justify-start gap-1">
                <p className="h6 font-semibold text-[#666666]">INFOLINKA</p>
                <span className="h5 font-semibold tracking-tight text-primary">
                  {props.generalLayout?.contact?.phone}
                </span>
              </div>
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
